import React from 'react';
import {OutlinedTextFieldProps} from '@mui/material';
import TextField from 'components/inputs/TextField';
import {useController, Validate} from 'react-hook-form';
import {useMemoRef} from 'common/hooks/useMemoRef';
import {useDebouncedCallback} from 'common/hooks/useDebouncedCallback';
import {INPUT_CHANGE_DEBOUNCE_MS} from 'common/constants';
import {useRegisterLabel} from 'common/context/FormContext';

export type FormikTextFieldProps = Partial<OutlinedTextFieldProps> & {
   name: string;
   // @deprecated use disabled prop instead
   enabled?: boolean | any;
   variant?: 'outlined';
   validate?: Validate<string, any>;
   sanitizer?: (val: string) => string;
};

const HookFormTextField = (props: FormikTextFieldProps) => {
   const {
      name,
      enabled = true,
      variant = 'outlined',
      placeholder,
      className,
      validate,
      required,
      sanitizer,
      onChange: _onChange,
      ...otherProps
   } = props;

   const {
      field: {onChange: _onChangeHook, onBlur: _onBlur, value, ref},
      fieldState: {error: _error},
   } = useController({
      name,
      // label: otherProps.label,
      rules: {
         required: required,
         validate: validate as Validate<string, any>,
      },
   });
   useRegisterLabel(name, (placeholder || otherProps.label) as string);

   const error = _error?.message;

   const disabled = !enabled;
   const label = placeholder;

   const [debVal, setDebVal] = React.useState<string>(value);
   const debouncedChange = useDebouncedCallback(_onChangeHook, INPUT_CHANGE_DEBOUNCE_MS);

   // handle reset
   React.useEffect(() => {
      setDebVal(value);
   }, [value]);

   const onChange = React.useCallback(
      (ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
         let newEv = ev;
         setDebVal(newEv.target.value);
         debouncedChange(newEv);
         // _onChangeHook(newEv);
         _onChange?.(newEv);
      },
      [_onChange, sanitizer]
   );

   const onBlur = React.useCallback(
      (ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
         debouncedChange.cancel();
         let newEv = ev;
         if (sanitizer) {
            const val = sanitizer ? sanitizer(ev.target.value) : ev.target.value;
            newEv = {
               ...ev,
               target: {
                  ...ev.target,
                  value: val,
               },
            };
         }
         _onChangeHook(newEv);
         _onBlur();
      },
      [debouncedChange, _onChangeHook, _onBlur]
   );

   const onEnterPress = React.useCallback(
      (e) => {
         e.key === 'Enter' && onBlur((e as unknown) as any);
      },
      [onBlur]
   );

   const _ref = useMemoRef(ref);
   // console.debug('HookFormTextField', name, error);

   return (
      <TextField
         onChange={onChange} // send value to hook form
         onBlur={onBlur} // notify when input is touched/blur
         onKeyPress={onEnterPress}
         // value={value || ''} // input value
         value={debVal ?? ''} // input value
         name={name} // send down the input name
         inputRef={_ref} // send input ref, so we can focus on input when error appear
         fullWidth
         error={!!error}
         disabled={disabled}
         label={label}
         helperText={error}
         variant={variant}
         {...otherProps}
      />
   );
};

export default React.memo(HookFormTextField);
